import type { LocationDescriptor, LocationDescriptorObject } from 'history';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import type { NavLinkProps } from 'react-router-dom';
import { NavLink, Link as RouterLink, useLocation } from 'react-router-dom';
import URLParse, { qs } from 'url-parse';

import type { RoutingKey } from '@swf/core/enums';
import useTrackers from '@swf/core/hooks/useTrackers';
import { select } from '@swf/core/store';
import type { ValuesProp } from '@swf/core/types/Props';
import { absoluteURL } from '@swf/core/utils';
import { generatePath } from '@swf/core/utils/paths';

export interface Props extends Partial<NavLinkProps>, Partial<ValuesProp> {
  activeClassName?: string;
  isActive?: NavLinkProps['isActive'];
  href?: string;
  route?: RoutingKey;
  searchParams?: URLSearchParams;
  track?: boolean;
}

const Link: React.FC<Props> = ({
  href = '',
  values,
  route,
  isActive,
  activeClassName,
  onClick,
  searchParams,
  track,
  ...props
}) => {
  const { search } = useLocation();
  const { routes } = useSelector(select.page.getMeta);
  const locale = useSelector(select.locale.get);
  const { siteId } = qs.parse(search) as { siteId?: string };
  const { linkClicked } = useTrackers();
  const linkRef = useRef<HTMLAnchorElement>(null);
  let target: string | LocationDescriptor = href;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    onClick && onClick(event);
    track !== false &&
      linkClicked({
        destinationUrl: (target as LocationDescriptorObject).pathname || (target as string),
        linkText: locale === 'en' ? linkRef.current?.innerText : undefined
      });
  };

  if (absoluteURL(href) || href.startsWith('#')) {
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    return <a {...props} href={href} onClick={handleClick} />;
  }

  if (route) {
    if (!routes || !routes[route]) {
      return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    target = generatePath(routes[route], values as any);
  }

  if (siteId || searchParams) {
    const targetUrl = new URLParse(target);
    const targetUrlParams = qs.stringify({
      // The types for URLParse.query are invalid. It's actually not an object, but a string
      ...qs.parse(targetUrl.query as unknown as string),
      ...qs.parse(searchParams?.toString() ?? ''),
      ...(siteId?.length ? { siteId } : {})
    });

    target = { pathname: targetUrl.pathname, search: targetUrlParams };
  }

  return activeClassName ? (
    <NavLink
      {...props}
      to={target}
      activeClassName={activeClassName}
      isActive={isActive}
      ref={linkRef}
      onClick={handleClick}
    />
  ) : (
    <RouterLink {...props} to={target} ref={linkRef} onClick={handleClick} />
  );
};

export default Link;
